import type {
  AssetTemplates,
  IShopifyPageAssignedData,
  ShopifyAsset,
  ShopifyAssetResponse,
} from '~/modules/shopify/types';
import type { IThemePage } from '~/stores/themePage';

import { getTemplateSuffixBySuffix } from './templateHelper';
import { parseTemplateInfoBy } from './templateParser';
import { TemplateType } from '~/types/template';

export const getAllAssets = (assets?: ShopifyAssetResponse): ShopifyAsset[] => {
  if (!assets) return [];
  return [...assets.templateGPV7, ...assets.templateGPV6, ...assets.templateShopify];
};

export const filterAssetsByType = <T = any>(asset: ShopifyAsset, activePageType: T) => {
  return asset.key?.includes(`templates/${activePageType}`);
};

export const extractAssetKeys = (asset: ShopifyAsset) => {
  return parseTemplateInfoBy({ templateSuffix: asset.key })?.suffix || '';
};

/**
 * Maps the bare suffix of every template the theme actually holds a file for to that template's
 * asset key — e.g. `templates/product.custom-name.json` is keyed by `custom-name`, matching the
 * bare form `PlatformProduct.templateSuffix` reports, so the full key can be looked back up from it.
 */
export const toTemplateSuffixes = (assets?: ShopifyAssetResponse): Map<string, string> => {
  const templateSuffixes = new Map<string, string>();

  for (const template of getAllAssets(assets)) {
    const suffix = extractAssetKeys(template);
    if (suffix) templateSuffixes.set(suffix, template.key);
  }

  return templateSuffixes;
};

/** Every template the theme holds a file for, by asset key — the spelling a variant records its own in. */
export const toTemplateAssetKeys = (assets?: ShopifyAssetResponse): Set<string> =>
  new Set(getAllAssets(assets).flatMap((template) => (template.key ? [template.key] : [])));

/**
 * The template a page renders, as the asset key templates are matched on. A page whose suffix has no
 * file left in the theme is rendered by Shopify with that type's default template, so that is what it
 * has to be matched against. The type is part of the answer, not just the suffix: `product.gp-a` and
 * `page.gp-a` are two different templates that a store can hold at once.
 */
export const toRenderedTemplateSuffix = ({
  type,
  templateSuffix,
  templateAssetKeys,
}: {
  type: TemplateType;
  templateSuffix?: string | null;
  /**
   * The theme's own templates. Undefined when the caller holds none, and so cannot tell a live
   * template from a deleted one — the page then reports no template rather than a guessed one.
   */
  templateAssetKeys?: Set<string>;
}): string | undefined => {
  if (!templateAssetKeys) return undefined;

  const assetKey = getTemplateSuffixBySuffix(type, templateSuffix ?? '');

  return templateAssetKeys.has(assetKey) ? assetKey : getTemplateSuffixBySuffix(type, '');
};

/**
 * The template the storefront serves at `/`, as the asset key templates are matched on. The homepage
 * is the one page with no record to read a suffix off: whichever home template is set as default
 * renders there, and Shopify's own `templates/index.json` does when none is — which is also why a
 * home template that is not default can be tested but never picked as Control.
 */
export const toRenderedHomeTemplateSuffix = ({
  themePages,
  templateAssetKeys,
}: {
  themePages?: IThemePage[];
  /** See `toRenderedTemplateSuffix` — undefined reports no template rather than a guessed one. */
  templateAssetKeys?: Set<string>;
}): string | undefined => {
  if (!templateAssetKeys) return undefined;

  // `parseTemplateInfoBy` reads Shopify's own template as the GemPages one standing in for it
  // wherever a theme page of that type is default, which is what makes the default the answer here.
  const templateInfo = parseTemplateInfoBy({
    templateSuffix: getTemplateSuffixBySuffix(TemplateType.HOME, ''),
    themePages,
  });

  return toRenderedTemplateSuffix({
    type: TemplateType.HOME,
    templateSuffix: templateInfo?.suffix,
    templateAssetKeys,
  });
};

/**
 * The template a product renders, as the asset key templates are matched on. A product whose suffix
 * has no file left in the theme is rendered by Shopify with the default product template, so that's
 * the template its page has to be matched against.
 */
export const toRenderedProductTemplateSuffix = ({
  templateSuffix,
  templateSuffixes,
}: {
  templateSuffix?: string;
  templateSuffixes: Map<string, string>;
}): string =>
  (templateSuffix && templateSuffixes.get(templateSuffix)) ||
  getTemplateSuffixBySuffix(TemplateType.PRODUCT, '');

export const updateTemplateAssignedTotal = ({
  templates,
  url,
  total,
  data,
}: {
  templates: AssetTemplates;
  url?: string;
  total: number;
  data: IShopifyPageAssignedData[];
}): AssetTemplates => {
  const templateOriginIdx = templates.findIndex(
    (asset) => asset.providerSource === 'SHOPIFY_ORIGIN',
  );
  const templateDefaultIdx = templates.findIndex((asset) => asset.themePageInfo?.default);

  const templateOrigin = templateOriginIdx !== -1 ? templates[templateOriginIdx] : null;
  const templateDefault = templateDefaultIdx !== -1 ? templates[templateDefaultIdx] : null;
  const updatedTemplates = [...templates];

  if (templateOrigin) {
    const updatedTotal = (templateOrigin.assigned?.total ?? 0) + total;
    const updatedData = (templateOrigin.assigned?.data ?? []).concat(data);
    const updatedTemplateOrigin = {
      ...templateOrigin,
      assigned: {
        url: templateOrigin.assigned?.url ?? url ?? '',
        total: updatedTotal,
        data: updatedData ?? [],
      },
    };

    updatedTemplates[templateOriginIdx] = updatedTemplateOrigin;
  }

  if (templateDefault) {
    const updatedTotal = (templateDefault.assigned?.total ?? 0) + total;
    const updatedData = (templateDefault.assigned?.data ?? []).concat(data);
    const updatedTemplateDefault = {
      ...templateDefault,
      assigned: {
        url: templateDefault.assigned?.url ?? url ?? '',
        total: updatedTotal,
        data: updatedData,
      },
    };
    updatedTemplates[templateDefaultIdx] = updatedTemplateDefault;
  }

  return updatedTemplates;
};
